/**
 * PublicScoreboardPage.jsx
 * Live scoreboard: Group standings (sport-wise) → Encounters → Individual ties
 */
import React, { useEffect, useState, useCallback } from 'react';
import { VERSION_LABEL } from '../version';
import { useParams } from 'react-router-dom';
import {
  Trophy, RefreshCw, Clock, ChevronDown, ChevronUp,
  Radio, CalendarDays, CheckCircle2, GitBranch
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Toaster } from '@/components/ui/sonner';
import api from '@/lib/api';
import SportIcon, { SPORT_LABELS } from '../components/SportIcon';

const SPORT_ICONS = {
  table_tennis:'🏓', badminton:'🏸', chess:'♟️',
  carrom:'🎯', pickleball:'🥒', squash:'🎾', cricket:'🏏', football:'⚽', default:'🏅',
};
const SPORT_NAMES = {
  table_tennis:'TT', badminton:'Bad', chess:'Chess',
  carrom:'Carrom', pickleball:'PB', squash:'Squash',
};

const fmtTime = (iso) => iso
  ? new Date(iso).toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit', hour12:true })
  : '';
const fmtDate = (iso) => iso
  ? new Date(iso).toLocaleDateString('en-IN', { day:'2-digit', month:'short' })
  : '';

/** Formats team name with seed: "Asawa Dhadkans (A1)" or just "A1" for TBD */
const teamWithSeed = (name, seed) => {
  if (!name || name === 'TBD') return seed || 'TBD';
  return seed ? `${name} (${seed})` : name;
};

// ── Encounter detail drill-down ───────────────────────────────
function EncounterDetail({ enc, sport }) {
  const [expanded, setExpanded]       = useState({});
  const [matchExpanded, setMatchExp]  = useState({}); // per-match expand
  const isDone = enc.status === 'completed';
  const isLive = enc.status === 'in_progress';

  const fmtDT = (iso) => iso
    ? new Date(iso).toLocaleString('en-IN', {
        day: 'numeric', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit', hour12: true,
        timeZone: 'Asia/Kolkata',
      })
    : null;

  // Filter to only the active sport if one is provided
  const visibleResults = sport
    ? (enc.sport_results || []).filter(sr => sr.sport_code === sport)
    : (enc.sport_results || []);

  return (
    <div className="mt-3 space-y-2">
      {visibleResults.map(sr => {
        const isOpen = expanded[sr.sport_code];
        const homeW = sr.home_match_wins ?? 0;
        const awayW = sr.away_match_wins ?? 0;
        return (
          <div key={sr.sport_code} className="bg-[#0f1219] rounded-xl border border-[#1e2435] overflow-hidden">
            {/* Sport row */}
            <button onClick={() => setExpanded(p=>({...p,[sr.sport_code]:!isOpen}))}
              className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-[#161a26] transition-colors">
              <span className="text-base"><SportIcon sport={sr.sport_code} size={22}/></span>
              <span className="text-xs font-semibold text-[#f0f2f7] capitalize flex-1 text-left">
                {sr.sport_code.replace(/_/g,' ')}
              </span>
              <span className={`text-xs font-bold tabular-nums ${homeW>awayW?'text-[#22c55e]':homeW<awayW?'text-[#ef4444]':'text-[#8a93a3]'}`}>
                {homeW} – {awayW}
              </span>
              {sr.sport_winner && sr.sport_winner !== 'tie_pending' && (
                <Badge className={`text-[9px] ml-1 ${sr.sport_winner==='draw'?'bg-[rgba(245,197,24,0.12)] text-[#f5c518]':sr.sport_winner==='home'?'bg-[rgba(34,197,94,0.12)] text-[#22c55e]':'bg-[rgba(239,68,68,0.12)] text-[#ef4444]'}`}>
                  {sr.sport_winner==='draw'?'Draw':sr.sport_winner==='home'?`${enc.home_team?.short_name||enc.home_team?.name?.split(' ')[0]||'H'} wins`:`${enc.away_team?.short_name||enc.away_team?.name?.split(' ')[0]||'A'} wins`}
                </Badge>
              )}
              {isOpen ? <ChevronUp className="h-3.5 w-3.5 text-[#8a93a3]"/> : <ChevronDown className="h-3.5 w-3.5 text-[#8a93a3]"/>}
            </button>

            {/* Individual matches */}
            {isOpen && (
              <div className="border-t border-[#1e2435]">
                {(sr.matches||[]).map((m,i) => {
                  const isResult  = ['chess','carrom'].includes(sr.sport_code);
                  const isTT      = sr.sport_code === 'table_tennis';
                  // Detect doubles by type field OR by having 2+ players in home_players array
                  const isDoubles = m.type === 'doubles' || (m.home_players?.length > 1);
                  const isPlat    = m.slot?.[0] === 'P' || m.tier === 'platinum';
                  const sc        = isPlat ? '#f5c518' : '#3b82f6';
                  // For doubles: always show both players (P1 / P2) from home_players array
                  // For singles: use home_player name
                  const hp = isDoubles && (m.home_players?.length > 1)
                    ? m.home_players.slice(0,2).join(' / ')
                    : (m.home_player || m.home_players?.[0] || null);
                  const ap = isDoubles && (m.away_players?.length > 1)
                    ? m.away_players.slice(0,2).join(' / ')
                    : (m.away_player || m.away_players?.[0] || null);
                  const mKey = `${sr.sport_code}_${i}`;
                  const isMatchOpen = !!matchExpanded[mKey];

                  const scoreDisplay = () => {
                    if (!m.winner) return '–';
                    if (isResult) return m.winner==='home'?'W – L':m.winner==='away'?'L – W':m.winner==='draw'?'D – D':'–';
                    if (isTT) {
                      const games = (m.games||[]).filter(g=>g.home||g.away);
                      if (games.length>0) return games.map(g=>`${g.home}–${g.away}`).join('  ');
                    }
                    return m.home_score!=null ? `${m.home_score}–${m.away_score}` : '–';
                  };

                  const homeWon = m.winner==='home'||m.winner==='bye_home';
                  const awayWon = m.winner==='away'||m.winner==='bye_away';
                  const dateStr = fmtDT(enc.scheduled_at);

                  return (
                    <div key={i} className="border-b border-[#1e2435]/60 last:border-0">
                      {/* Clickable match row */}
                      <button
                        className="w-full text-left flex items-center gap-2 px-3 py-2.5 text-xs hover:bg-[#161a26] transition-colors cursor-pointer"
                        onClick={() => setMatchExp(p=>({...p,[mKey]:!isMatchOpen}))}>
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded flex-shrink-0"
                          style={{backgroundColor:`${sc}15`,color:sc}}>
                          {m.slot||m.label||`M${i+1}`}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <span className={`truncate flex-1 min-w-0 ${homeWon?'text-[#22c55e] font-semibold':'text-[#8a93a3]'}`}>
                              {/* For doubles, show first player only in collapsed row */}
                              {isDoubles
                                ? (m.home_players?.[0] || (hp?.split(' / ')[0]) || hp || enc.home_team?.name?.split(' ')[0] || 'Home')
                                : (hp || enc.home_team?.name?.split(' ')[0] || 'Home')}
                            </span>
                            <span className={`font-black tabular-nums flex-shrink-0 ${
                              isResult
                                ? (m.winner==='home'?'text-[#22c55e]':m.winner==='away'?'text-[#ef4444]':m.winner==='draw'?'text-[#f5c518]':'text-[#8a93a3]')
                                : 'text-[#f5c518]'
                            }`}>
                              {scoreDisplay()}
                            </span>
                            <span className={`truncate flex-1 min-w-0 text-right ${awayWon?'text-[#22c55e] font-semibold':'text-[#8a93a3]'}`}>
                              {/* For doubles, show first player only in collapsed row */}
                              {isDoubles
                                ? (m.away_players?.[0] || (ap?.split(' / ')[0]) || ap || enc.away_team?.name?.split(' ')[0] || 'Away')
                                : (ap || enc.away_team?.name?.split(' ')[0] || 'Away')}
                            </span>
                          </div>
                          {(m.winner==='bye_home'||m.winner==='bye_away') && (
                            <p className="text-[#f5c518] text-[9px] mt-0.5">BYE awarded</p>
                          )}
                        </div>
                        <ChevronDown className={`h-3 w-3 text-[#8a93a3] flex-shrink-0 ml-1 transition-transform ${isMatchOpen?'rotate-180':''}`}/>
                      </button>

                      {/* Expanded match detail */}
                      {isMatchOpen && (
                        <div className="px-3 pb-3 bg-[#0a0c13] border-t border-[#1e2435]/40">
                          {/* Winner headline — stacked for doubles, side-by-side for singles */}
                          {isDoubles ? (
                            /* Doubles: vertical layout — full pair names readable on mobile */
                            <div className="mt-2 mb-2 px-2 py-2 bg-[#161a26] rounded-xl space-y-1.5">
                              <div className="flex items-center justify-between gap-2">
                                <p className={`text-sm font-black truncate flex-1 ${homeWon?'text-[#22c55e]':'text-[#f0f2f7]'}`}>
                                  {hp || enc.home_team?.name?.split(' ')[0] || 'Home'}
                                </p>
                                {homeWon && <span className="text-[10px] text-[#22c55e] font-bold flex-shrink-0">🏆 Winner</span>}
                              </div>
                              <div className="flex items-center justify-center gap-3">
                                <span className="text-xs text-[#8a93a3]">vs</span>
                                <span className="text-base font-black text-[#f5c518] tabular-nums">{scoreDisplay()}</span>
                                <span className="text-[9px] text-[#8a93a3]">{sr.sport_code.replace(/_/g,' ')}</span>
                              </div>
                              <div className="flex items-center justify-between gap-2">
                                <p className={`text-sm font-black truncate flex-1 ${awayWon?'text-[#22c55e]':'text-[#f0f2f7]'}`}>
                                  {ap || enc.away_team?.name?.split(' ')[0] || 'Away'}
                                </p>
                                {awayWon && <span className="text-[10px] text-[#22c55e] font-bold flex-shrink-0">🏆 Winner</span>}
                              </div>
                            </div>
                          ) : (
                            /* Singles: original three-column layout */
                            <div className="flex items-center justify-between mt-2 mb-2 px-2 py-2 bg-[#161a26] rounded-xl">
                              <div className="flex-1 text-center">
                                <p className={`text-sm font-black truncate ${homeWon?'text-[#22c55e]':'text-[#f0f2f7]'}`}>
                                  {hp || enc.home_team?.name?.split(' ')[0] || 'Home'}
                                </p>
                                {homeWon && <p className="text-[9px] text-[#22c55e] font-bold">🏆 Winner</p>}
                              </div>
                              <div className="flex-shrink-0 px-3 text-center">
                                <p className="text-base font-black text-[#f5c518] tabular-nums">{scoreDisplay()}</p>
                                <p className="text-[9px] text-[#8a93a3]">{sr.sport_code.replace(/_/g,' ')}</p>
                              </div>
                              <div className="flex-1 text-center">
                                <p className={`text-sm font-black truncate ${awayWon?'text-[#22c55e]':'text-[#f0f2f7]'}`}>
                                  {ap || enc.away_team?.name?.split(' ')[0] || 'Away'}
                                </p>
                                {awayWon && <p className="text-[9px] text-[#22c55e] font-bold">🏆 Winner</p>}
                              </div>
                            </div>
                          )}

                          {/* Game-by-game (TT) */}
                          {isTT && (m.games||[]).filter(g=>g.home||g.away).length>0 && (
                            <div className="flex items-center gap-1.5 flex-wrap mb-2">
                              <span className="text-[9px] text-[#8a93a3] uppercase tracking-wide">Games:</span>
                              {(m.games||[]).filter(g=>g.home||g.away).map((g,gi)=>(
                                <span key={gi} className="text-[10px] font-mono bg-[#1d2230] border border-[#252a37] px-1.5 py-0.5 rounded text-[#f0f2f7]">
                                  {g.home}–{g.away}
                                </span>
                              ))}
                            </div>
                          )}

                          {/* Meta info */}
                          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-[#8a93a3]">
                            <span className="capitalize">{isDoubles ? 'Doubles' : (m.type||'Singles')}{isPlat?' · ★ Platinum':''}</span>
                            {enc.match_label && <span className="font-mono font-bold text-[#f5c518]">{enc.match_label}</span>}
                            {dateStr && <span>{dateStr}</span>}
                          </div>
                          {/* Scorer attribution */}
                          {enc.scored_by_name && enc.scored_by_name !== '?' && (
                            <p className="text-[9px] text-[#8a93a3]/60 mt-1">
                              Scored by: <span className="text-[#8a93a3]">{enc.scored_by_name}</span>
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Encounter card ────────────────────────────────────────────
function EncounterCard({ enc }) {
  const [open, setOpen] = useState(false);
  const isDone = enc.status === 'completed';
  const isLive = enc.status === 'in_progress';
  const isPending = enc.status === 'pending';
  const homeName = teamWithSeed(enc.home_team?.name, enc.home_seed);
  const awayName = teamWithSeed(enc.away_team?.name, enc.away_seed);
  const homeColor = enc.home_team?.color || '#3b82f6';
  const awayColor = enc.away_team?.color || '#ef4444';
  const sports = enc.sport_results || [];

  return (
    <div className={`rounded-2xl border overflow-hidden transition-all ${
      isLive ? 'border-[rgba(239,68,68,0.4)] bg-[#161a26] shadow-[0_0_24px_rgba(239,68,68,0.06)]'
             : 'border-[#252a37] bg-[#161a26]'
    }`}>
      {/* Header */}
      <button onClick={() => setOpen(!open)} className="w-full text-left">
        <div className="px-4 pt-3 pb-1.5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {isLive && (
              <span className="flex items-center gap-1 text-[#ef4444] text-[10px] font-bold">
                <span className="w-1.5 h-1.5 bg-[#ef4444] rounded-full animate-pulse"/>LIVE
              </span>
            )}
            {isDone && <span className="text-[10px] text-[#22c55e] font-medium">✓ Final</span>}
            {enc.match_label && <span className="text-[10px] text-[#8a93a3] font-mono">{enc.match_label}</span>}
            {enc.scheduled_at && <span className="text-[10px] text-[#8a93a3] flex items-center gap-0.5">
              <Clock className="h-2.5 w-2.5"/>{fmtDate(enc.scheduled_at)} {fmtTime(enc.scheduled_at)}
            </span>}
          </div>
          <div className="flex items-center gap-2">
            {/* Sport icons */}
            <div className="flex gap-0.5">
              {sports.map(s=><span key={s.sport_code} className="text-sm" title={s.sport_code}><SportIcon sport={s.sport_code} size={22}/></span>)}
            </div>
            {open ? <ChevronUp className="h-4 w-4 text-[#8a93a3]"/> : <ChevronDown className="h-4 w-4 text-[#8a93a3]"/>}
          </div>
        </div>

        {/* Teams + score */}
        <div className="px-4 pb-3 flex items-center gap-3">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="h-9 w-9 rounded-xl flex-shrink-0 flex items-center justify-center font-bold text-white text-sm"
              style={{backgroundColor: homeColor}}>
              {enc.home_team?.logo_url?.startsWith('data:')
                ? <img src={enc.home_team.logo_url} className="h-full w-full rounded-xl object-cover" alt=""/>
                : (enc.home_team?.short_name?.[0] || homeName[0])}
            </div>
            <p className={`font-bold text-sm truncate ${enc.winner==='home'?'text-[#f5c518]':'text-[#f0f2f7]'}`}>{homeName}</p>
          </div>

          <div className="text-center flex-shrink-0 min-w-[80px]">
            {(isDone || isLive) ? (
              <p className="text-2xl font-black tabular-nums text-[#f5c518]">
                {enc.total_home_pts ?? 0} : {enc.total_away_pts ?? 0}
              </p>
            ) : (
              <p className="text-[#8a93a3] font-bold">vs</p>
            )}
            {enc.winner && <p className="text-[10px] text-[#8a93a3]">pts</p>}
          </div>

          <div className="flex items-center gap-2 flex-1 min-w-0 justify-end">
            <p className={`font-bold text-sm truncate text-right ${enc.winner==='away'?'text-[#f5c518]':'text-[#f0f2f7]'}`}>{awayName}</p>
            <div className="h-9 w-9 rounded-xl flex-shrink-0 flex items-center justify-center font-bold text-white text-sm"
              style={{backgroundColor: awayColor}}>
              {enc.away_team?.logo_url?.startsWith('data:')
                ? <img src={enc.away_team.logo_url} className="h-full w-full rounded-xl object-cover" alt=""/>
                : (enc.away_team?.short_name?.[0] || awayName[0])}
            </div>
          </div>
        </div>

        {/* Sport score summary strip */}
        {(isDone || isLive) && sports.length > 0 && (
          <div className="px-4 pb-2.5 flex gap-2 flex-wrap">
            {sports.filter(s=>s.sport_winner).map(s=>(
              <span key={s.sport_code} className="text-[10px] flex items-center gap-1 bg-[#1d2230] rounded-full px-2 py-0.5">
                <span><SportIcon sport={s.sport_code} size={22}/></span>
                <span className="text-[#8a93a3]">{s.home_match_wins}–{s.away_match_wins}</span>
                <span className={`font-semibold ${s.sport_winner==='home'?'text-[#22c55e]':s.sport_winner==='away'?'text-[#ef4444]':'text-[#f5c518]'}`}>
                  {s.sport_winner==='draw'?'D':s.sport_winner==='home'?'H':'A'}
                </span>
              </span>
            ))}
          </div>
        )}
      </button>

      {/* Drill-down detail */}
      {open && (
        <div className="border-t border-[#252a37] px-3 pb-3">
          <EncounterDetail enc={enc}/>
        </div>
      )}
    </div>
  );
}

// SPORT_LABELS imported from SportIcon component above

// ── Team encounter drilldown ──────────────────────────────────
function TeamDrilldown({ teamId, teamName, tid, onClose, sport, allEncounters }) {
  const [openEncId, setOpenEncId] = useState(null);

  // Use parent's already-fetched encounters (auto-refreshes every 30s with parent)
  // Fall back to local fetch only if allEncounters not provided
  const [fetchedEncs, setFetchedEncs] = useState(null);
  useEffect(() => {
    if (allEncounters) return; // parent provides data — no need to fetch
    fetch(`/api/public/tournaments/${tid}/encounters`)
      .then(r => r.ok ? r.json() : [])
      .then(data => setFetchedEncs(data))
      .catch(() => setFetchedEncs([]));
  }, [tid, allEncounters]); // eslint-disable-line react-hooks/exhaustive-deps

  const source = allEncounters || fetchedEncs || [];
  const loading = !allEncounters && fetchedEncs === null;
  const encs = source
    .filter(e => e.home_team_id === teamId || e.away_team_id === teamId)
    .sort((a, b) => (a.scheduled_at||'').localeCompare(b.scheduled_at||''));

  return (
    <div className="mt-2 mb-3 mx-1 bg-[#06080d] border border-[rgba(245,197,24,0.25)] rounded-xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-[rgba(245,197,24,0.15)]">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold text-[#f5c518]">{teamName}</span>
          <span className="text-[10px] text-[#8a93a3]">— All Ties</span>
        </div>
        <button onClick={onClose} className="text-[#8a93a3] hover:text-[#f0f2f7] text-xs px-1">✕</button>
      </div>

      {loading ? (
        <div className="p-4 text-center text-xs text-[#8a93a3]">Loading…</div>
      ) : encs.length === 0 ? (
        <div className="p-4 text-center text-xs text-[#8a93a3]">No ties found</div>
      ) : (
        <div className="divide-y divide-[#1e2435]">
          {encs.map(enc => {
            const mySide   = enc.home_team_id === teamId ? 'home' : 'away';
            const oppSide  = mySide === 'home' ? 'away' : 'home';
            const oppTeam  = enc[`${oppSide}_team`] || {};
            const oppName  = teamWithSeed(oppTeam.name, enc[`${oppSide}_seed`]);
            const myPts    = enc[`total_${mySide}_pts`] ?? enc[`${mySide}_pts`] ?? null;
            const oppPts   = enc[`total_${oppSide}_pts`] ?? enc[`${oppSide}_pts`] ?? null;
            const encWin   = enc.winner;
            const result   = !encWin ? null : encWin === mySide ? 'W' : encWin === 'draw' ? 'D' : 'L';
            const rc       = result === 'W' ? '#22c55e' : result === 'L' ? '#ef4444' : result === 'D' ? '#f5c518' : '#8a93a3';
            const isOpen   = openEncId === enc.id;
            const isDone   = enc.status === 'completed';

            // Sport summary chips — filter by active sport if set
            const sportChips = (enc.sport_results || []).filter(sr => sr.sport_winner && (!sport || sr.sport_code === sport));

            return (
              <div key={enc.id}>
                {/* Clickable encounter row */}
                <button
                  className="w-full text-left px-3 py-3 hover:bg-[#0f1219] transition-colors flex items-center gap-2"
                  onClick={() => setOpenEncId(isOpen ? null : enc.id)}>
                  {/* Result badge */}
                  {result ? (
                    <span className="h-7 w-7 rounded-lg flex-shrink-0 flex items-center justify-center text-xs font-black"
                      style={{ backgroundColor: rc+'20', color: rc }}>
                      {result}
                    </span>
                  ) : (
                    <span className="h-7 w-7 rounded-lg flex-shrink-0 flex items-center justify-center text-[9px] font-bold bg-[#1d2230] text-[#8a93a3]">
                      —
                    </span>
                  )}
                  {/* Opponent logo + name */}
                  <div className="h-6 w-6 rounded-md flex-shrink-0 flex items-center justify-center text-[9px] font-bold text-white overflow-hidden"
                    style={{ backgroundColor: oppTeam.color||'#3b82f6' }}>
                    {oppTeam.logo_url?.startsWith('data:')
                      ? <img src={oppTeam.logo_url} className="h-full w-full object-cover" alt=""/>
                      : (oppTeam.name?.[0] || '?')}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-[#f0f2f7] truncate">vs {oppName}</p>
                    {sportChips.length > 0 && (
                      <div className="flex gap-1 mt-0.5 flex-wrap">
                        {sportChips.map(sr => {
                          const sw = sr.sport_winner;
                          const myW = sw === mySide ? 'W' : sw === 'draw' ? 'D' : 'L';
                          const c = myW==='W'?'#22c55e':myW==='L'?'#ef4444':'#f5c518';
                          return (
                            <span key={sr.sport_code} className="text-[8px] font-bold px-1 py-0.5 rounded flex items-center gap-0.5"
                              style={{backgroundColor:c+'20',color:c}}>
                              <SportIcon sport={sr.sport_code} size={10}/>
                              {myW} {sr[`${mySide}_match_wins`]??0}-{sr[`${oppSide}_match_wins`]??0}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  {/* Total score */}
                  {myPts !== null && (
                    <span className="text-sm font-black tabular-nums flex-shrink-0" style={{ color: rc }}>
                      {myPts}–{oppPts}
                    </span>
                  )}
                  <ChevronDown className={`h-3 w-3 text-[#8a93a3] flex-shrink-0 transition-transform ${isOpen?'rotate-180':''}`}/>
                </button>

                {/* Expanded: full encounter detail */}
                {isOpen && (
                  <div className="border-t border-[#1e2435]">
                    <EncounterDetail enc={enc} sport={sport}/>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


// ── Per-sport group card (one group, one sport) ─────────────────
function SportLeagueGroupCard({ group, sport, tid, sfTeams, finalists, winners, allEncounters, hasSemis }) {
  const [drillTeam, setDrillTeam] = useState(null);
  // Sort by sport wins desc, then sport pts as tiebreak
  const sorted = [...(group.standings || [])].sort((a, b) => {
    const aw = a.sport_wld?.[sport]?.wins ?? Math.floor((a.sport_pts?.[sport]||0)/2);
    const bw = b.sport_wld?.[sport]?.wins ?? Math.floor((b.sport_pts?.[sport]||0)/2);
    if (bw !== aw) return bw - aw;
    return (b.sport_pts?.[sport]||0) - (a.sport_pts?.[sport]||0);
  });

  const GROUP_DOT = { A:'#ef4444', B:'#3b82f6', C:'#22c55e', D:'#f97316' };

  return (
    <div className="bg-[#161a26] rounded-2xl border border-[#252a37] overflow-hidden">
      {/* Group header */}
      <div className="px-4 py-2.5 bg-[#10131c] border-b border-[#252a37] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: GROUP_DOT[group.group] || '#f5c518' }}/>
          <span className="text-sm font-bold text-[#f0f2f7]">Group {group.group}</span>
        </div>
        <span className="text-[10px] text-[#22c55e] font-semibold">Top 2 → {hasSemis ? 'Semis' : 'Final'}</span>
      </div>

      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-[#252a37] bg-[#0c0f18]">
            <th className="text-left px-3 py-2 text-[#8a93a3] w-6">#</th>
            <th className="text-left px-3 py-2 text-[#8a93a3]">Team</th>
            <th className="text-center px-2 py-2 text-[#22c55e] font-bold">W</th>
            <th className="text-center px-2 py-2 text-[#ef4444] font-bold">L</th>
            <th className="text-center px-3 py-2 text-[#f5c518] font-bold">Win%</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((row, i) => {
            const wld    = row.sport_wld?.[sport];
            const wins   = wld?.wins   ?? Math.floor((row.sport_pts?.[sport]||0)/2);
            const losses = wld?.losses ?? 0;
            const draws  = wld?.draws  ?? 0;
            const played = wins + losses + draws;
            const pct    = played > 0 ? Math.round(wins/played*100) : 0;

            const qualifies = i < 2;
            const isSF    = sfTeams?.has(row.team_id);
            const isFin   = finalists?.has(row.team_id);
            const isWin   = winners === row.team_id;
            const isExp   = drillTeam?.id === row.team_id;

            // Highest badge wins
            const badge = isWin  ? <span className="text-base leading-none">🏆</span>
                        : isFin  ? <span className="text-[9px] font-black text-[#f5c518] bg-[rgba(245,197,24,0.15)] px-1.5 py-0.5 rounded-full border border-[rgba(245,197,24,0.3)] whitespace-nowrap">FINAL</span>
                        : (isSF || qualifies) ? <span className="text-[9px] font-black text-[#22c55e] bg-[rgba(34,197,94,0.12)] px-1.5 py-0.5 rounded-full border border-[rgba(34,197,94,0.25)] whitespace-nowrap">SF</span>
                        : null;

            return (
              <React.Fragment key={row.team_id}>
                <tr
                  onClick={() => setDrillTeam(isExp ? null : { id: row.team_id, name: row.team_name })}
                  className={`border-b border-[#252a37]/50 last:border-0 cursor-pointer transition-colors
                    ${isExp ? 'bg-[#1d2230]'
                    : qualifies ? 'bg-[rgba(34,197,94,0.04)] hover:bg-[rgba(34,197,94,0.08)]'
                    : 'hover:bg-[#1d2230]'}`}>
                  <td className="px-3 py-3 text-center">
                    <span className={`text-xs font-bold ${qualifies ? 'text-[#22c55e]' : 'text-[#8a93a3]/50'}`}>{i+1}</span>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="h-6 w-6 rounded-lg flex-shrink-0 flex items-center justify-center font-bold text-[9px] text-white overflow-hidden"
                        style={{ backgroundColor: row.color||'#3b82f6' }}>
                        {row.logo_url?.startsWith('data:')
                          ? <img src={row.logo_url} className="h-full w-full rounded-lg object-cover" alt=""/>
                          : row.team_name?.[0]}
                      </div>
                      <span className={`font-semibold truncate ${isWin?'text-[#f5c518]':qualifies?'text-[#f0f2f7]':'text-[#8a93a3]'}`}>
                        {row.team_name}
                      </span>
                      {badge && <span className="flex-shrink-0 ml-1">{badge}</span>}
                    </div>
                  </td>
                  <td className="px-2 py-3 text-center font-bold text-[#22c55e] tabular-nums">{wins}</td>
                  <td className="px-2 py-3 text-center font-bold text-[#ef4444] tabular-nums">{losses}</td>
                  <td className="px-3 py-3 text-center font-black text-[#f5c518] tabular-nums">{played>0?`${pct}%`:'—'}</td>
                </tr>
                {isExp && (
                  <tr><td colSpan={5} className="p-0">
                    <TeamDrilldown teamId={row.team_id} teamName={row.team_name} tid={tid} sport={sport} allEncounters={allEncounters} onClose={()=>setDrillTeam(null)}/>
                  </td></tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}


// ── Overall combined standings (all groups merged, top 2 highlighted) ────
function OverallCombinedCard({ groups }) {
  // Merge all groups into one sorted list by pts
  const allTeams = groups.flatMap(g => (g.standings||[]).map(r => ({
    ...r, group: g.group
  }))).sort((a,b) => (b.pts??0)-(a.pts??0) || (b.wins??0)-(a.wins??0));
  const GROUP_DOT = { A:'#ef4444', B:'#3b82f6', C:'#22c55e', D:'#f97316' };
  return (
    <div className="bg-[#161a26] rounded-2xl border border-[#252a37] overflow-hidden">
      <div className="px-4 py-2.5 bg-[#10131c] border-b border-[#252a37] flex items-center justify-between">
        <span className="text-sm font-bold text-[#f0f2f7]">Overall Standings</span>
        <span className="text-[10px] text-[#22c55e] font-semibold">Top 2 → Finals</span>
      </div>
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-[#252a37] bg-[#0c0f18]">
            <th className="text-left px-3 py-2 text-[#8a93a3] w-6">#</th>
            <th className="text-left px-3 py-2 text-[#8a93a3]">Team</th>
            <th className="text-center px-2 py-2 text-[#8a93a3]">Grp</th>
            <th className="text-center px-2 py-2 text-[#8a93a3]">P</th>
            <th className="text-center px-2 py-2 text-[#22c55e] font-bold">W</th>
            <th className="text-center px-2 py-2 text-[#ef4444] font-bold">L</th>
            <th className="text-center px-3 py-2 text-[#f5c518] font-bold">Pts</th>
          </tr>
        </thead>
        <tbody>
          {allTeams.map((row, i) => {
            const qualifies = i < 2;
            return (
              <tr key={row.team_id}
                className={`border-b border-[#252a37]/50 last:border-0 ${qualifies?'bg-[rgba(34,197,94,0.04)]':''}`}>
                <td className="px-3 py-3 text-center">
                  <span className={`text-xs font-bold ${qualifies?'text-[#22c55e]':'text-[#8a93a3]/50'}`}>{i+1}</span>
                </td>
                <td className="px-3 py-3">
                  <div className="flex items-center gap-2">
                    <div className="h-6 w-6 rounded-lg flex-shrink-0 flex items-center justify-center font-bold text-[9px] text-white overflow-hidden"
                      style={{ backgroundColor: row.color||'#3b82f6' }}>
                      {row.logo_url?.startsWith('data:')
                        ? <img src={row.logo_url} className="h-full w-full rounded-lg object-cover" alt=""/>
                        : row.team_name?.[0]}
                    </div>
                    <span className={`font-semibold ${qualifies?'text-[#f0f2f7]':'text-[#8a93a3]'}`}>{row.team_name}</span>
                    {qualifies && <span className="text-[9px] font-black text-[#22c55e] bg-[rgba(34,197,94,0.12)] px-1.5 py-0.5 rounded-full border border-[rgba(34,197,94,0.25)] ml-1">Finals</span>}
                  </div>
                </td>
                <td className="px-2 py-3 text-center">
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{color: GROUP_DOT[row.group]||'#8a93a3', backgroundColor: `${GROUP_DOT[row.group]||'#8a93a3'}20`}}>
                    {row.group}
                  </span>
                </td>
                <td className="px-2 py-3 text-center text-[#8a93a3]">{row.played??0}</td>
                <td className="px-2 py-3 text-center font-bold text-[#22c55e]">{row.wins??0}</td>
                <td className="px-2 py-3 text-center font-bold text-[#ef4444]">{row.losses??0}</td>
                <td className="px-3 py-3 text-center font-black text-[#f5c518] text-sm">{row.pts??0}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="px-4 py-2 border-t border-[#252a37]/50 bg-[#0c0f18]">
        <p className="text-[10px] text-[#8a93a3] text-center">2 pts per encounter win · All groups combined · Top 2 advance to Finals</p>
      </div>
    </div>
  );
}
function OverallGroupCard({ group, hasSemis }) {
  const standings = group.standings || [];
  const GROUP_DOT = { A:'#ef4444', B:'#3b82f6', C:'#22c55e', D:'#f97316' };
  return (
    <div className="bg-[#161a26] rounded-2xl border border-[#252a37] overflow-hidden">
      <div className="px-4 py-2.5 bg-[#10131c] border-b border-[#252a37] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: GROUP_DOT[group.group] || '#f5c518' }}/>
          <span className="text-sm font-bold text-[#f0f2f7]">Group {group.group}</span>
        </div>
        <span className="text-[10px] text-[#22c55e] font-semibold">Top 2 → {hasSemis ? 'Semis' : 'Final'}</span>
      </div>
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-[#252a37] bg-[#0c0f18]">
            <th className="text-left px-3 py-2 text-[#8a93a3] w-6">#</th>
            <th className="text-left px-3 py-2 text-[#8a93a3]">Team</th>
            <th className="text-center px-2 py-2 text-[#8a93a3]">P</th>
            <th className="text-center px-2 py-2 text-[#22c55e] font-bold">W</th>
            <th className="text-center px-2 py-2 text-[#ef4444] font-bold">L</th>
            <th className="text-center px-2 py-2 text-[#3b82f6] font-bold" title="Point Differential (scored − conceded)">+/−</th>
            <th className="text-center px-3 py-2 text-[#f5c518] font-bold">Pts</th>
          </tr>
        </thead>
        <tbody>
          {standings.map((row, i) => {
            const qualifies = i < 2;
            const diff = row.pt_diff ?? (row.sport_pts_for ?? 0) - (row.sport_pts_against ?? 0);
            return (
              <tr key={row.team_id}
                className={`border-b border-[#252a37]/50 last:border-0 transition-colors
                  ${qualifies ? 'bg-[rgba(34,197,94,0.04)]' : ''}`}>
                <td className="px-3 py-3 text-center">
                  <span className={`text-xs font-bold ${qualifies?'text-[#22c55e]':'text-[#8a93a3]/50'}`}>{i+1}</span>
                </td>
                <td className="px-3 py-3">
                  <div className="flex items-center gap-2">
                    <div className="h-6 w-6 rounded-lg flex-shrink-0 flex items-center justify-center font-bold text-[9px] text-white overflow-hidden"
                      style={{ backgroundColor: row.color||'#3b82f6' }}>
                      {row.logo_url?.startsWith('data:')
                        ? <img src={row.logo_url} className="h-full w-full rounded-lg object-cover" alt=""/>
                        : row.team_name?.[0]}
                    </div>
                    <span className={`font-semibold ${qualifies?'text-[#f0f2f7]':'text-[#8a93a3]'}`}>{row.team_name}</span>
                    {qualifies && <span className="text-[9px] font-black text-[#22c55e] bg-[rgba(34,197,94,0.12)] px-1.5 py-0.5 rounded-full border border-[rgba(34,197,94,0.25)] ml-1">SF</span>}
                  </div>
                </td>
                <td className="px-2 py-3 text-center text-[#8a93a3] tabular-nums">{row.played ?? 0}</td>
                <td className="px-2 py-3 text-center font-bold text-[#22c55e] tabular-nums">{row.wins ?? 0}</td>
                <td className="px-2 py-3 text-center font-bold text-[#ef4444] tabular-nums">{row.losses ?? 0}</td>
                <td className={`px-2 py-3 text-center font-bold tabular-nums text-xs ${diff > 0 ? 'text-[#22c55e]' : diff < 0 ? 'text-[#ef4444]' : 'text-[#8a93a3]'}`}>
                  {diff > 0 ? '+' : ''}{diff}
                </td>
                <td className="px-3 py-3 text-center font-black text-[#f5c518] tabular-nums text-sm">{row.pts ?? 0}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="px-4 py-2 border-t border-[#252a37]/50 bg-[#0c0f18]">
        <p className="text-[10px] text-[#8a93a3] text-center">2 pts per encounter win · Tiebreaker: point differential · Top 2 advance to Finals</p>
      </div>
    </div>
  );
}

// ── Points group card (sport-by-sport match pts breakdown) ──────
function PointsGroupCard({ group, sports, koEncs, allGroupEncs }) {
  // Merge KO encounter match points into each team's sport_pts
  // Use individual match wins × 2 pts (not sport-level win pts) for consistency with group stage
  const koPtsByTeam = {}; // teamId → { sportCode: extraPts }
  (koEncs || []).forEach(e => {
    if (e.status !== 'completed') return;
    for (const sr of (e.sport_results || [])) {
      const sc = sr.sport_code;
      // Count individual match wins × 2 pts each
      let hp = 0, ap = 0;
      for (const m of (sr.matches || [])) {
        if (m.winner === 'home' || m.winner === 'bye_home')       { hp += 2; }
        else if (m.winner === 'away' || m.winner === 'bye_away')  { ap += 2; }
        else if (m.winner === 'draw')                             { hp += 1; ap += 1; }
      }
      // Fallback to stored pts if matches aren't detailed yet
      if (hp === 0 && ap === 0) { hp = sr.home_pts ?? 0; ap = sr.away_pts ?? 0; }
      if (e.home_team_id && hp > 0) {
        if (!koPtsByTeam[e.home_team_id]) koPtsByTeam[e.home_team_id] = {};
        koPtsByTeam[e.home_team_id][sc] = (koPtsByTeam[e.home_team_id][sc] || 0) + hp;
      }
      if (e.away_team_id && ap > 0) {
        if (!koPtsByTeam[e.away_team_id]) koPtsByTeam[e.away_team_id] = {};
        koPtsByTeam[e.away_team_id][sc] = (koPtsByTeam[e.away_team_id][sc] || 0) + ap;
      }
    }
  });

  const hasKOPoints = Object.keys(koPtsByTeam).length > 0;

  const standings = [...(group.standings || [])].map(row => {
    const extra = koPtsByTeam[row.team_id] || {};
    const mergedPts = { ...(row.sport_pts || {}) };
    let koTotal = 0;
    for (const [sc, pts] of Object.entries(extra)) {
      mergedPts[sc] = (mergedPts[sc] || 0) + pts;
      koTotal += pts;
    }
    return { ...row, sport_pts: mergedPts, total_pts: (row.total_pts ?? row.pts ?? 0) + koTotal };
  }).sort((a, b) => (b.total_pts ?? b.pts ?? 0) - (a.total_pts ?? a.pts ?? 0));

  const GROUP_DOT = { A:'#ef4444', B:'#3b82f6', C:'#22c55e', D:'#f97316' };
  return (
    <div className="bg-[#161a26] rounded-2xl border border-[#252a37] overflow-hidden">
      <div className="px-4 py-2.5 bg-[#10131c] border-b border-[#252a37] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: GROUP_DOT[group.group] || '#f5c518' }}/>
          <span className="text-sm font-bold text-[#f0f2f7]">Group {group.group}</span>
        </div>
        <span className="text-[10px] text-[#8a93a3] font-medium">Individual match points</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-[#252a37] bg-[#0c0f18]">
              <th className="text-left px-3 py-2 text-[#8a93a3] w-6">#</th>
              <th className="text-left px-3 py-2 text-[#8a93a3]">Team</th>
              {sports.map(sc => (
                <th key={sc} className="text-center px-2 py-2 text-[#8a93a3]" title={SPORT_LABELS[sc]||sc}>
                  <SportIcon sport={sc} size={54}/>
                </th>
              ))}
              <th className="text-center px-3 py-2 text-[#f5c518] font-bold whitespace-nowrap">Total</th>
            </tr>
          </thead>
          <tbody>
            {standings.map((row, i) => {
              const total = row.total_pts ?? row.pts ?? 0;
              return (
                <tr key={row.team_id}
                  className={`border-b border-[#252a37]/50 last:border-0 ${i===0?'bg-[rgba(245,197,24,0.04)]':''}`}>
                  <td className="px-3 py-3 text-center">
                    <span className={`text-xs font-bold ${i===0?'text-[#f5c518]':i===1?'text-[#8a93a3]':'text-[#8a93a3]/50'}`}>{i+1}</span>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2">
                      <div className="h-6 w-6 rounded-lg flex-shrink-0 flex items-center justify-center font-bold text-[9px] text-white overflow-hidden"
                        style={{ backgroundColor: row.color||'#3b82f6' }}>
                        {row.logo_url?.startsWith('data:')
                          ? <img src={row.logo_url} className="h-full w-full rounded-lg object-cover" alt=""/>
                          : row.team_name?.[0]}
                      </div>
                      <span className={`font-semibold ${i===0?'text-[#f5c518]':'text-[#f0f2f7]'}`}>{row.team_name}</span>
                    </div>
                  </td>
                  {sports.map(sc => (
                    <td key={sc} className="px-2 py-3 text-center text-[#8a93a3] tabular-nums">
                      {row.sport_pts?.[sc] ?? 0}
                    </td>
                  ))}
                  <td className="px-3 py-3 text-center font-black text-[#f5c518] tabular-nums text-sm">{total}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-2 border-t border-[#252a37]/50 bg-[#0c0f18]">
        <p className="text-[10px] text-[#8a93a3] text-center">
          {(() => {
            // Compute max league pts: encounters × matches_per_encounter × 2pts per win
            const sampleEnc = (allGroupEncs || []).find(e => (e.sport_results||[]).length > 0);
            const matchesPerEnc = sampleEnc ? (sampleEnc.sport_results[0]?.matches?.length || 0) : 0;
            const totalEncs = (allGroupEncs || []).length;
            const maxLeaguePts = totalEncs * matchesPerEnc * 2;
            const koMaxPts = hasKOPoints ? Math.round(maxLeaguePts / 2) : 0;
            return `2 pts per individual match win · League: ${maxLeaguePts > 0 ? maxLeaguePts : '—'} pts${hasKOPoints ? ` · +${koMaxPts} pts from Finals` : ''}`;
          })()}
        </p>
      </div>
    </div>
  );
}

// ── Bracket match card ────────────────────────────────────────
const BCARD_W = 188; // px — fixed so SVG connectors align perfectly
const BCARD_H = 92;  // px — two team rows + header

function BracketMatchBox({ enc, roundLabel }) {
  const [open, setOpen] = React.useState(false);

  if (!enc) {
    // Placeholder for a not-yet-available slot
    return (
      <div style={{ width: BCARD_W, minHeight: BCARD_H }}
        className="rounded-xl border border-dashed border-[#252a37] bg-[#161a26] flex items-center justify-center">
        <span className="text-[#4a5568] text-xs font-semibold">TBD</span>
      </div>
    );
  }
  const isDone   = enc.status === 'completed';
  const isLive   = enc.status === 'in_progress';
  const homeWon  = isDone && enc.winner === 'home';
  const awayWon  = isDone && enc.winner === 'away';
  const homeName = teamWithSeed(enc.home_team?.name, enc.home_seed);
  const awayName = teamWithSeed(enc.away_team?.name, enc.away_seed);
  const homeColor = enc.home_team?.color || '#3b82f6';
  const awayColor = enc.away_team?.color || '#ef4444';
  const homeIsTeam = !!enc.home_team?.name;
  const awayIsTeam = !!enc.away_team?.name;

  const TeamRow = ({ name, color, isTeam, logoUrl, won, score, side }) => (
    <div className={`flex items-center gap-2 px-3 py-2 flex-1 ${won ? 'bg-[rgba(245,197,24,0.05)]' : ''}`}>
      {isTeam ? (
        <div className="h-6 w-6 rounded-lg flex-shrink-0 flex items-center justify-center font-bold text-[10px] text-white overflow-hidden"
          style={{ backgroundColor: color }}>
          {logoUrl?.startsWith('data:')
            ? <img src={logoUrl} className="h-full w-full object-cover rounded-lg" alt=""/>
            : name[0]}
        </div>
      ) : (
        <div className="h-6 w-6 rounded-lg flex-shrink-0 flex items-center justify-center text-[10px] font-bold bg-[#1d2230] border border-dashed border-[#3a4155] text-[#4a5568]">
          {name[0] || '?'}
        </div>
      )}
      <span className={`flex-1 text-xs font-semibold truncate ${won ? 'text-[#f5c518]' : isTeam ? 'text-[#f0f2f7]' : 'text-[#4a5568]'}`}>
        {name}
      </span>
      {(isDone || isLive) && score !== undefined && score !== null && (
        <span className={`text-sm font-black tabular-nums flex-shrink-0 ${won ? 'text-[#f5c518]' : 'text-[#8a93a3]'}`}>
          {score}
        </span>
      )}
      {won && <Trophy className="h-3 w-3 text-[#f5c518] flex-shrink-0"/>}
    </div>
  );

  return (
    <div style={{ width: BCARD_W }} className={`rounded-xl border overflow-hidden flex flex-col cursor-pointer ${
      isLive ? 'border-[rgba(239,68,68,0.45)] shadow-[0_0_16px_rgba(239,68,68,0.08)]' :
      isDone ? 'border-[#252a37]' : 'border-[#252a37]'
    } bg-[#161a26]`} onClick={() => setOpen(o => !o)}>
      {/* Header */}
      <div className="px-3 py-1.5 border-b border-[#1e2435] flex items-center justify-between bg-[#10131c]">
        <span className="text-[10px] font-bold text-[#8a93a3] uppercase tracking-wider">
          {roundLabel || enc.match_label || 'Match'}
        </span>
        <div className="flex items-center gap-1.5">
          {isLive && (
            <span className="flex items-center gap-1 text-[#ef4444] text-[10px] font-bold">
              <span className="w-1.5 h-1.5 bg-[#ef4444] rounded-full animate-pulse"/>LIVE
            </span>
          )}
          {isDone && <CheckCircle2 className="h-3 w-3 text-[#22c55e]"/>}
          <ChevronDown className={`h-3 w-3 text-[#8a93a3] transition-transform ${open?'rotate-180':''}`}/>
        </div>
      </div>
      {/* Teams */}
      <div className="flex flex-col divide-y divide-[#1e2435]/50 flex-1">
        <TeamRow name={homeName} color={homeColor} isTeam={homeIsTeam}
          logoUrl={enc.home_team?.logo_url} won={homeWon}
          score={enc.total_home_pts} side="home"/>
        <TeamRow name={awayName} color={awayColor} isTeam={awayIsTeam}
          logoUrl={enc.away_team?.logo_url} won={awayWon}
          score={enc.total_away_pts} side="away"/>
      </div>
      {/* Sport summary strip */}
      {isDone && (enc.sport_results || []).some(s => s.sport_winner) && (
        <div className="px-3 py-1.5 border-t border-[#1e2435] bg-[#10131c] flex gap-1.5 flex-wrap">
          {(enc.sport_results || []).filter(s=>s.sport_winner).map(s=>(
            <span key={s.sport_code} className="text-[9px] flex items-center gap-0.5 bg-[#1d2230] rounded-full px-1.5 py-0.5">
              <SportIcon sport={s.sport_code} size={14}/>
              <span className="text-[#8a93a3]">{s.home_match_wins}–{s.away_match_wins}</span>
              <span className={`font-bold ${s.sport_winner==='home'?'text-[#22c55e]':s.sport_winner==='away'?'text-[#ef4444]':'text-[#f5c518]'}`}>
                {s.sport_winner==='draw'?'D':s.sport_winner==='home'?'H':'A'}
              </span>
            </span>
          ))}
        </div>
      )}
      {/* Drill-down: show sport-by-sport match details on click */}
      {open && <div onClick={e => e.stopPropagation()}>
        <EncounterDetail enc={enc}/>
      </div>}
    </div>
  );
}

// ── Public knockout bracket ───────────────────────────────────
function PublicBracket({ encounters }) {
  const sfEncs = encounters
    .filter(e => e.round_type === 'sf')
    .sort((a, b) => (a.match_label || '').localeCompare(b.match_label || ''));
  const finalEnc  = encounters.find(e => e.round_type === 'final');
  const thirdEnc  = encounters.find(e => e.round_type === '3rd');

  if (sfEncs.length === 0 && !finalEnc) {
    return (
      <div className="text-center py-16">
        <GitBranch className="h-10 w-10 text-[#8a93a3] mx-auto mb-3"/>
        <p className="text-[#8a93a3] font-semibold">Knockout bracket not available yet</p>
        <p className="text-xs text-[#8a93a3] mt-1">Appears once the group stage is complete</p>
      </div>
    );
  }

  // Layout math — fixed card height for precise SVG connector alignment
  const SF_GAP  = 32;   // px gap between SF1 and SF2 cards
  const CONN_W  = 40;   // px connector column width
  // Actual rendered card height (header ≈ 28, each team row ≈ 36, sport strip ≈ 0 unless done)
  // Use BCARD_H as our canonical reference
  const totalSfH  = sfEncs.length > 1 ? BCARD_H * 2 + SF_GAP : BCARD_H;
  const sf1CY     = BCARD_H / 2;
  const sf2CY     = sfEncs.length > 1 ? BCARD_H + SF_GAP + BCARD_H / 2 : BCARD_H / 2;
  const midX      = CONN_W / 2;
  const finalCY   = sfEncs.length > 1 ? totalSfH / 2 : BCARD_H / 2;
  const finalOffsetTop = sfEncs.length > 1 ? finalCY - BCARD_H / 2 : 0;
  const champion  = finalEnc?.winner
    ? (finalEnc.winner === 'home'
        ? teamWithSeed(finalEnc.home_team?.name, finalEnc.home_seed)
        : teamWithSeed(finalEnc.away_team?.name, finalEnc.away_seed))
    : null;

  return (
    <div>
      {/* Champion banner */}
      {champion && (
        <div className="mb-5 px-4 py-3 bg-[rgba(245,197,24,0.08)] border border-[rgba(245,197,24,0.3)] rounded-2xl flex items-center gap-3">
          <span className="text-3xl">🏆</span>
          <div>
            <p className="text-[10px] text-[#8a93a3] uppercase tracking-widest font-bold">Champion</p>
            <p className="text-lg font-black text-[#f5c518]">{champion}</p>
          </div>
        </div>
      )}

      {/* Bracket tree */}
      <div className="overflow-x-auto pb-2">
        <div className="inline-flex items-start gap-0 min-w-max">
          {/* ── Semi Finals column ── */}
          {sfEncs.length > 0 && (
            <div>
              <p className="text-[10px] font-bold text-[#8a93a3] uppercase tracking-wider mb-3 text-center">
                Semi Finals
              </p>
              <div className="flex flex-col" style={{ gap: SF_GAP }}>
                {sfEncs.map(enc => (
                  <BracketMatchBox key={enc.id} enc={enc} roundLabel={enc.match_label || 'Semi Final'}/>
                ))}
              </div>
            </div>
          )}

          {/* ── SVG Connector ── */}
          {sfEncs.length > 0 && (finalEnc || sfEncs.length > 0) && (
            <div style={{ paddingTop: 23, flexShrink: 0 }}>
              <svg width={CONN_W} height={totalSfH} style={{ display: 'block', overflow: 'visible' }}>
                {/* SF1 → midX */}
                <path d={`M 0,${sf1CY} H ${midX}`}
                  fill="none" stroke="#3a4155" strokeWidth="1.5" strokeLinecap="round"/>
                {/* SF2 → midX */}
                {sfEncs.length > 1 && (
                  <path d={`M 0,${sf2CY} H ${midX}`}
                    fill="none" stroke="#3a4155" strokeWidth="1.5" strokeLinecap="round"/>
                )}
                {/* Vertical bridge */}
                {sfEncs.length > 1 && (
                  <path d={`M ${midX},${sf1CY} V ${sf2CY}`}
                    fill="none" stroke="#3a4155" strokeWidth="1.5" strokeLinecap="round"/>
                )}
                {/* midX → Final */}
                <path d={`M ${midX},${finalCY} H ${CONN_W}`}
                  fill="none" stroke="#3a4155" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </div>
          )}

          {/* ── Final column ── */}
          {finalEnc && (
            <div>
              <p className="text-[10px] font-bold text-[#f5c518] uppercase tracking-wider mb-3 text-center">
                Final
              </p>
              <div style={{ paddingTop: finalOffsetTop }}>
                <BracketMatchBox enc={finalEnc} roundLabel={finalEnc.match_label || 'Final'}/>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 3rd place (if any) */}
      {thirdEnc && (
        <div className="mt-5 pt-5 border-t border-[#1e2435]">
          <p className="text-[10px] font-bold text-[#8a93a3] uppercase tracking-wider mb-3 text-center">3rd Place Play-off</p>
          <div className="flex justify-center">
            <BracketMatchBox enc={thirdEnc} roundLabel={thirdEnc.match_label || '3rd Place'}/>
          </div>
        </div>
      )}

      {/* Upcoming matches list (if bracket not started yet) */}
      {!finalEnc && sfEncs.length > 0 && (
        <div className="mt-4 space-y-2">
          {sfEncs.map(enc => <EncounterCard key={enc.id} enc={enc}/>)}
        </div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────
export default function PublicScoreboardPage() {
  const { tournamentId } = useParams();
  const [tournament, setTournament]     = useState(null);
  const [groupStandings, setGroupStandings] = useState([]);
  const [encounters, setEncounters]     = useState([]);
  const [sponsors, setSponsors]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [activeTab, setActiveTab]       = useState('standings');
  const [activeSportTab, setActiveSportTab] = useState(null); // null = first sport auto-selected
  const [lastRefresh, setLastRefresh]   = useState(new Date());

  const fetchAll = useCallback(async () => {
    try {
      const [tRes, gsRes, encRes, spRes] = await Promise.allSettled([
        api.get(`/public/tournaments/${tournamentId}`),
        api.get(`/public/tournaments/${tournamentId}/group-standings`),
        api.get(`/public/tournaments/${tournamentId}/encounters`),
        api.get(`/public/tournaments/${tournamentId}/sponsors`),
      ]);
      if (tRes.status === 'fulfilled')   setTournament(tRes.value.data);
      if (gsRes.status === 'fulfilled') {
        const gs = Array.isArray(gsRes.value.data) ? gsRes.value.data : [];
        setGroupStandings(gs);
      }
      if (encRes.status === 'fulfilled') setEncounters(Array.isArray(encRes.value.data) ? encRes.value.data : []);
      if (spRes.status === 'fulfilled')  setSponsors(Array.isArray(spRes.value.data) ? spRes.value.data : []);
      setLastRefresh(new Date());
    } catch (_) {}
    finally { setLoading(false); }
  }, [tournamentId]);

  useEffect(() => {
    fetchAll();
    const iv = setInterval(() => fetchAll(), 30000);
    return () => clearInterval(iv);
  }, [fetchAll]);

  // Auto-select first sport when standings load (separate from fetchAll to avoid stale closure)
  useEffect(() => {
    if (!activeSportTab && groupStandings.length > 0) {
      const sports = [...new Set(groupStandings.flatMap(g => g.sports || []))];
      if (sports.length > 0) setActiveSportTab(sports[0]);
    }
  }, [groupStandings]); // eslint-disable-line react-hooks/exhaustive-deps

  const liveEncs    = encounters.filter(e => e.status === 'in_progress');
  const groupEncs   = encounters.filter(e => e.round_type === 'group').sort((a,b)=>(a.scheduled_at||'').localeCompare(b.scheduled_at||''));
  const koEncs      = encounters.filter(e => ['sf','final','3rd'].includes(e.round_type));
  const allSports   = [...new Set(groupStandings.flatMap(g => g.sports || []))];

  // ── Badge data derived from KO encounters ──────────────────────
  const sfTeamsBySport = {};
  const finalistsBySport = {};
  const winnersBySport = {};
  koEncs.forEach(e => {
    const sc = e.sport_code; if (!sc) return;
    if (e.round_type === 'sf') {
      if (!sfTeamsBySport[sc]) sfTeamsBySport[sc] = new Set();
      if (e.home_team_id) sfTeamsBySport[sc].add(e.home_team_id);
      if (e.away_team_id) sfTeamsBySport[sc].add(e.away_team_id);
    }
    if (e.round_type === 'final') {
      if (!finalistsBySport[sc]) finalistsBySport[sc] = new Set();
      if (e.home_team_id) finalistsBySport[sc].add(e.home_team_id);
      if (e.away_team_id) finalistsBySport[sc].add(e.away_team_id);
      if (e.winner === 'home' && e.home_team_id) winnersBySport[sc] = e.home_team_id;
      if (e.winner === 'away' && e.away_team_id) winnersBySport[sc] = e.away_team_id;
    }
  });
  // Overall champion: most sport finals won (points as tiebreak)
  const sportWinsCount = {};
  Object.values(winnersBySport).forEach(tid => { sportWinsCount[tid] = (sportWinsCount[tid]||0)+1; });
  // Whether any SF encounters exist (determines "→ Semis" vs "→ Final" labels)
  const hasSfEncounters = koEncs.some(e => e.round_type === 'sf');
  let championTeam = null;
  const topEntry = Object.entries(sportWinsCount).sort((a,b)=>b[1]-a[1])[0];
  if (topEntry) {
    for (const g of groupStandings) {
      const row = g.standings.find(r => r.team_id === topEntry[0]);
      if (row) { championTeam = { ...row, sportsWon: topEntry[1] }; break; }
    }
  }
  // ──────────────────────────────────────────────────────────────
  // Prefer title sponsor WITH a logo; fall back to any title sponsor
  const titleSponsor = sponsors?.find(s =>
    (s.type_id === 'title' || s.type_id?.toLowerCase().includes('title')) &&
    (s.logo_url || s.team_logo_url)
  ) || sponsors?.find(s =>
    s.type_id === 'title' || s.type_id?.toLowerCase().includes('title')
  ) || null;

  const TABS = [
    { id:'standings', label:'Standings', count: groupStandings.length },
    { id:'points',    label:'Points' },
    { id:'live',      label:'Live',      count: liveEncs.length, pulse: liveEncs.length > 0 },
    { id:'matches',   label:'Matches',   count: groupEncs.length },
  ];

  return (
    <div className="min-h-screen bg-[#06080d] text-[#f0f2f7]">
      <Toaster position="top-center"/>

      {/* Header */}
      <div className="bg-[#10131c] border-b border-[#1e2435] sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="min-w-0">
            {loading ? <div className="h-5 w-48 bg-[#1d2230] rounded animate-pulse"/> : (
              <>
                {titleSponsor ? (
                  <div className="flex items-center gap-2 mb-0.5">
                    {(titleSponsor.logo_url || titleSponsor.team_logo_url) && (
                      <img
                        src={titleSponsor.logo_url || titleSponsor.team_logo_url}
                        className="h-12 w-12 rounded-lg object-contain flex-shrink-0 bg-white p-0.5"
                        alt={titleSponsor.name}
                        onError={e => { e.currentTarget.style.display='none'; }}
                      />
                    )}
                    <span className="text-[10px] font-bold text-[#f5c518] uppercase tracking-wider">
                      {titleSponsor.name} presents
                    </span>
                  </div>
                ) : null}
                <h1 className="text-base font-bold text-[#f0f2f7] truncate">{tournament?.name || 'Scoreboard'}</h1>
                <p className="text-[10px] text-[#8a93a3]">Updated {lastRefresh.toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit',timeZone:'Asia/Kolkata'})}</p>
              </>
            )}
          </div>
          <button onClick={fetchAll} className="p-2 rounded-lg text-[#8a93a3] hover:text-[#f0f2f7] hover:bg-[#1d2230] transition-colors">
            <RefreshCw className="h-4 w-4"/>
          </button>
        </div>

        {/* Tab bar */}
        <div className="max-w-2xl mx-auto px-4 pb-0 flex gap-0 border-t border-[#1e2435] overflow-x-auto scrollbar-none">
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-2.5 text-xs font-semibold border-b-2 transition-colors whitespace-nowrap ${
                activeTab===tab.id
                  ? 'border-[#f5c518] text-[#f5c518]'
                  : 'border-transparent text-[#8a93a3] hover:text-[#f0f2f7]'
              }`}>
              {tab.pulse && <span className="w-1.5 h-1.5 bg-[#ef4444] rounded-full animate-pulse"/>}
              {tab.label}
              {tab.count > 0 && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${activeTab===tab.id?'bg-[rgba(245,197,24,0.15)] text-[#f5c518]':'bg-[#1d2230] text-[#8a93a3]'}`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-4 space-y-3">
        {loading ? (
          [1,2,3].map(i=><Skeleton key={i} className="h-32 bg-[#161a26] rounded-2xl"/>)
        ) : (
          <>
            {/* STANDINGS */}
            {activeTab === 'standings' && (
              groupStandings.length === 0
                ? <div className="text-center py-16 text-[#8a93a3]">Standings not available yet</div>
                : (
                  <div>
                    {/* Sport selector pills — no Overall, just sports */}
                    <div className="flex gap-2 overflow-x-auto pb-3 -mx-1 px-1 scrollbar-none">
                      {allSports.map(sc => (
                        <button key={sc} onClick={() => setActiveSportTab(sc)}
                          className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${
                            activeSportTab === sc
                              ? 'bg-[#f5c518] text-[#0b0f18] border-[#f5c518]'
                              : 'bg-[#161a26] text-[#8a93a3] border-[#252a37] hover:text-[#f0f2f7]'
                          }`}>
                          <SportIcon sport={sc} size={22} /> {SPORT_LABELS[sc]||sc}
                        </button>
                      ))}
                    </div>

                    {/* ── Overall (hidden — no pill) ─────────────── */}
                    {activeSportTab === 'all' && (
                      <div className="space-y-4">
                        {championTeam && (
                          <div className="px-4 py-3 bg-[rgba(245,197,24,0.08)] border border-[rgba(245,197,24,0.35)] rounded-2xl flex items-center gap-3">
                            <span className="text-3xl">🏆</span>
                            <div>
                              <p className="text-[10px] text-[#8a93a3] uppercase tracking-widest font-bold">Overall Champion</p>
                              <p className="text-lg font-black text-[#f5c518]">{championTeam.team_name}</p>
                              <p className="text-[10px] text-[#8a93a3]">{championTeam.sportsWon} sport{championTeam.sportsWon>1?'s':''} won</p>
                            </div>
                          </div>
                        )}
                        {/* Overall qualification mode: show single merged table */}
                        {groupStandings[0]?.qualification_mode === 'overall' ? (
                          <OverallCombinedCard groups={groupStandings}/>
                        ) : (
                          groupStandings.map(g => (
                            <OverallGroupCard key={g.group} group={g} hasSemis={hasSfEncounters} />
                          ))
                        )}
                      </div>
                    )}

                    {/* ── Per-sport: League tables + bracket ──────── */}
                    {!activeSportTab && allSports.length > 0 && (
                      <div className="text-center py-8 text-[#8a93a3] text-sm">Loading standings…</div>
                    )}
                    {activeSportTab && activeSportTab !== 'all' && (
                      <div className="space-y-4">
                        {/* Sport header */}
                        <div className="flex items-center gap-2.5">
                          <SportIcon sport={activeSportTab} size={36}/>
                          <h2 className="text-lg font-black text-[#f0f2f7]">{SPORT_LABELS[activeSportTab]||activeSportTab}</h2>
                          {winnersBySport[activeSportTab] && (() => {
                            const wid = winnersBySport[activeSportTab];
                            let wname = '';
                            for (const g of groupStandings) {
                              const r = g.standings.find(s=>s.team_id===wid);
                              if (r) { wname = r.team_name; break; }
                            }
                            return wname ? (
                              <span className="text-xs font-bold text-[#f5c518] bg-[rgba(245,197,24,0.12)] px-2 py-1 rounded-full border border-[rgba(245,197,24,0.3)] ml-1 flex items-center gap-1">
                                🏆 {wname}
                              </span>
                            ) : null;
                          })()}
                        </div>

                        {/* Group A + Group B tables for this sport */}
                        {groupStandings.map(g => (
                          <SportLeagueGroupCard
                            key={g.group}
                            group={g}
                            sport={activeSportTab}
                            tid={tournamentId}
                            sfTeams={sfTeamsBySport[activeSportTab]}
                            finalists={finalistsBySport[activeSportTab]}
                            winners={winnersBySport[activeSportTab]}
                            allEncounters={encounters}
                            hasSemis={hasSfEncounters}
                          />
                        ))}

                        {/* Bracket for this sport */}
                        {(() => {
                          const sportKo = koEncs.filter(e => (e.sport_code || 'all') === activeSportTab);
                          if (sportKo.length === 0) return null;
                          const hasSf = sportKo.some(e => e.round_type === 'sf');
                          return (
                            <div className="mt-2">
                              <div className="flex items-center gap-2 mb-3">
                                <div className="h-0.5 flex-1 bg-[#252a37] rounded-full"/>
                                <span className="text-[10px] font-bold text-[#8a93a3] uppercase tracking-widest px-2">{hasSf ? 'Semis & Final' : 'Final'}</span>
                                <div className="h-0.5 flex-1 bg-[#252a37] rounded-full"/>
                              </div>
                              <PublicBracket encounters={sportKo}/>
                            </div>
                          );
                        })()}

                        {/* No KO yet message */}
                        {koEncs.filter(e => (e.sport_code || 'all') === activeSportTab).length === 0 && (
                          <p className="text-[10px] text-[#8a93a3] text-center py-2">
                            Knockout bracket will appear once the group stage completes
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )
            )}

            {/* POINTS — sport-by-sport match points breakdown */}
            {activeTab === 'points' && (
              groupStandings.length === 0
                ? <div className="text-center py-16 text-[#8a93a3]">Points not available yet</div>
                : (
                  <div className="space-y-4">
                    {groupStandings.map(g => (
                      <PointsGroupCard key={g.group} group={g} sports={g.sports || allSports} koEncs={koEncs} allGroupEncs={groupEncs}/>
                    ))}
                  </div>
                )
            )}

            {/* LIVE */}
            {activeTab === 'live' && (
              liveEncs.length === 0
                ? (
                  <div className="text-center py-16">
                    <Radio className="h-10 w-10 text-[#8a93a3] mx-auto mb-3"/>
                    <p className="text-[#8a93a3]">No live matches right now</p>
                  </div>
                )
                : liveEncs.map(e => <EncounterCard key={e.id} enc={e}/>)
            )}

            {/* SCHEDULE tab */}
            {activeTab === 'schedule' && (() => {
              const schEncs = encounters.filter(e=>e.scheduled_at).sort((a,b)=>a.scheduled_at.localeCompare(b.scheduled_at));
              return schEncs.length === 0
                ? <div className="text-center py-16 text-[#8a93a3]">No schedule yet</div>
                : schEncs.map(e => <EncounterCard key={e.id} enc={e}/>);
            })()}

            {/* MATCHES (group) */}
            {activeTab === 'matches' && (
              groupEncs.length === 0
                ? <div className="text-center py-16 text-[#8a93a3]">No matches scheduled yet</div>
                : groupEncs.map(e => <EncounterCard key={e.id} enc={e}/>)
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <div className="max-w-2xl mx-auto px-4 py-6 text-center">
        <p className="text-[10px] text-[#8a93a3]">BTCCP Live Scoreboard · Auto-refreshes every 30s</p>
        <p className="text-[10px] text-[#8a93a3]/50 mt-1">Software by <span className="text-[#8a93a3]/70 font-medium">@Sandeep Maheshwari</span></p>
        <p className="text-[9px] text-[#8a93a3]/30 mt-0.5 font-mono">{VERSION_LABEL}</p>
      </div>
    </div>
  );
}
